import React, { useState, useEffect, useCallback } from 'react';
import Navbar from '../../components/Navbar';
import { adminAPI } from '../../services/api';

// Platform's own financial summary over a date range. Delivered orders only
// (money-realization point), by delivery date (IST). More metrics to come.
const inr = (n) => '₹' + (Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function todayISO(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

function Card({ title, value, sub, accent = '#0F172A', children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
      <div style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>{title}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color: accent, marginTop: 6 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{sub}</div>}
      {children}
    </div>
  );
}

function PersonalFinanceHub() {
  const [startDate, setStartDate] = useState(todayISO(-29));
  const [endDate, setEndDate] = useState(todayISO(0));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await adminAPI.getPersonalFinanceHub({ startDate, endDate });
      setData(res.data.data);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  const setPreset = (days) => { setStartDate(todayISO(-days + 1)); setEndDate(todayISO(0)); };

  const bd = (obj) => obj?.breakdown || {};

  return (
    <div style={{ background: '#f8fafc', minHeight: '100vh' }}>
      <Navbar />
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: 24 }}>
        <div style={{ marginBottom: 8 }}>
          <h1 style={{ fontSize: 24, fontWeight: 800, margin: 0 }}>💼 Personal Financial Hub</h1>
          <p style={{ color: '#64748b', margin: '4px 0 0', fontSize: 14 }}>
            Platform earnings & GST over a date range. {data?.basis ? <em>({data.basis})</em> : ''}
          </p>
        </div>

        {/* Date range */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, margin: '16px 0' }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>From</label>
            <input type="date" value={startDate} max={endDate} onChange={(e) => setStartDate(e.target.value)} style={{ padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14 }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>To</label>
            <input type="date" value={endDate} min={startDate} max={todayISO(0)} onChange={(e) => setEndDate(e.target.value)} style={{ padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14 }} />
          </div>
          <button onClick={load} style={{ padding: '9px 18px', background: '#f43f5e', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>Apply</button>
          <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
            {[{ l: 'Today', d: 1 }, { l: '7d', d: 7 }, { l: '30d', d: 30 }, { l: '90d', d: 90 }].map(p => (
              <button key={p.l} onClick={() => setPreset(p.d)} style={{ padding: '8px 12px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{p.l}</button>
            ))}
          </div>
        </div>

        {error && <div style={{ padding: 12, background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 8, marginBottom: 16 }}>{error}</div>}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>Loading…</div>
        ) : data ? (
          <>
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>
              <strong>{(data.orderCount || 0).toLocaleString('en-IN')}</strong> delivered orders in range
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
              <Card title="Total Delivery Fees Collected" value={inr(data.deliveryFees?.total)} accent="#2563eb"
                sub={`Average per order: ${inr(data.deliveryFees?.average)}`} />
              <Card title="Total Platform Fees Collected" value={inr(data.platformFees?.total)} accent="#7c3aed" />
              <Card title="Delivery Commission Collected" value={inr(data.deliveryCommission?.total)} accent="#0891b2"
                sub="Platform's cut from the delivery fee" />
              <Card title="Total GST Collected" value={inr(data.gstCollected?.total)} accent="#16a34a">
                <div style={{ marginTop: 10, fontSize: 12, color: '#475569', lineHeight: 1.7 }}>
                  <div>Food GST: <b>{inr(bd(data.gstCollected).foodGst)}</b></div>
                  <div>Platform-fee GST: <b>{inr(bd(data.gstCollected).platformFeeGst)}</b></div>
                  <div>Delivery-fee GST: <b>{inr(bd(data.gstCollected).deliveryFeeGst)}</b></div>
                </div>
              </Card>
              <Card title="Total GST Payable (to govt)" value={inr(data.gstPayable?.total)} accent="#dc2626">
                <div style={{ marginTop: 10, fontSize: 12, color: '#475569', lineHeight: 1.7 }}>
                  <div>Platform-fee GST: <b>{inr(bd(data.gstPayable).platformFeeGst)}</b></div>
                  <div>Delivery-fee GST: <b>{inr(bd(data.gstPayable).deliveryFeeGst)}</b></div>
                  <div>Food GST — Sec 9(5): <b>{inr(bd(data.gstPayable).section9_5FoodGst)}</b></div>
                </div>
              </Card>
            </div>
            <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 20, lineHeight: 1.6 }}>
              Figures use each order's authoritative pricing + earnings snapshot (taken at delivery).
              <br /><b>GST collected</b> = GST charged to customers on invoices. <b>GST payable</b> = the platform's
              remittance liability (platform-fee &amp; delivery GST + Sec 9(5) food GST). Payable can exceed collected
              when the platform funds a discount — the customer pays GST on the discounted value, but under Sec 9(5)
              the platform still owes GST on the full food value. More metrics will be added here.
            </p>
          </>
        ) : null}
      </div>
    </div>
  );
}

export default PersonalFinanceHub;
